/**
 * What a campaign's angle (ring R9) changes in the editor: the draft on a
 * switch or a preset, and which angle each part of the page speaks for.
 *
 * Its own file rather than more of `aiCampaigns.ts`, which mirrors the
 * server's campaign API and had reached the size limit. `aiCampaigns.ts`
 * re-exports everything here, so imports keep working. Only types come from
 * there, so the two files never load each other at runtime.
 *
 * React-free: `aiCampaignAngle.test.ts` runs it under plain node.
 */
import type {
  AiCallCampaign,
  CampaignAngle,
  CampaignAudience,
  CampaignAudiencePreview,
  CampaignDraft,
  CampaignGoal,
  CampaignKind,
} from "./aiCampaigns";

/** What a switch to an alert overwrites, kept on the draft for a switch back to an offer. */
export interface OfferFields {
  goal: CampaignGoal;
  openingQuestion: string;
  /** Absent when the audience made no client choice of its own. */
  includeClients?: boolean;
}

/**
 * The draft with another angle. Switching to an alert sets what an alert call
 * needs at once: the goal is the defect report, the question starts from the
 * alert default (an offer question rarely fits), and existing clients are left
 * out, as the server refuses to save an alert audience that includes them.
 *
 * What that switch overwrote is kept on the draft, and switching back to an
 * offer puts it back, so a mis-click on the angle never rewrites an offer's
 * goal, question or client choice. A draft loaded as an alert has nothing kept
 * and returns to an offer as it is.
 */
export function withAngle(d: CampaignDraft, angle: CampaignAngle): CampaignDraft {
  if (angle === d.angle) return d;
  const { offerBeforeAlert, ...rest } = d;
  if (angle === "offer") {
    if (!offerBeforeAlert) return { ...rest, angle };
    return {
      ...rest,
      angle,
      goal: offerBeforeAlert.goal,
      openingQuestion: offerBeforeAlert.openingQuestion,
      audience: withClientChoice(d.audience, offerBeforeAlert.includeClients),
    };
  }
  const kept: OfferFields = { goal: d.goal, openingQuestion: d.openingQuestion };
  if (d.audience.filter.includeClients !== undefined) kept.includeClients = d.audience.filter.includeClients;
  return { ...rest, angle, goal: "send_report", openingQuestion: "", audience: withoutClients(d.audience), offerBeforeAlert: kept };
}

/**
 * The draft started over from a preset, which picks whom to ring. On an alert
 * the goal stays the defect report; the preset's goal (and its lack of a
 * client choice) wait for a switch back to an offer.
 */
export function withPreset(
  d: CampaignDraft,
  preset: { kind: CampaignKind; audience: CampaignAudience; goal: CampaignGoal },
): CampaignDraft {
  if (d.angle !== "alert") return { ...d, kind: preset.kind, goal: preset.goal, audience: preset.audience };
  const offerBeforeAlert: OfferFields = { goal: preset.goal, openingQuestion: d.offerBeforeAlert?.openingQuestion ?? "" };
  return { ...d, kind: preset.kind, goal: "send_report", audience: withoutClients(preset.audience), offerBeforeAlert };
}

/** An audience with existing clients left out. The default is an absent key, as the audience card writes it. */
export function withoutClients(audience: CampaignAudience): CampaignAudience {
  if (audience.filter.includeClients === undefined) return audience;
  if (audience.source === "brain") {
    const { includeClients: _clients, ...filter } = audience.filter;
    return { source: "brain", filter };
  }
  const { includeClients: _clients, ...filter } = audience.filter;
  return { source: "register", filter };
}

/** An audience with a kept client choice put back; nothing kept leaves it as it is. */
function withClientChoice(audience: CampaignAudience, includeClients: boolean | undefined): CampaignAudience {
  if (includeClients === undefined) return audience;
  if (audience.source === "brain") return { source: "brain", filter: { ...audience.filter, includeClients } };
  return { source: "register", filter: { ...audience.filter, includeClients } };
}

/**
 * The warm campaign's warning that none of its callable companies heard from
 * us, so every opening uses the cold reason. An alert opening never uses a
 * reason slot (code says the defect sentence), so an alert campaign never
 * gets it.
 */
export function warnsNoneHeard(
  kind: CampaignKind,
  angle: CampaignAngle,
  preview: Pick<CampaignAudiencePreview, "callable" | "heardFromUs">,
): boolean {
  return angle !== "alert" && kind === "warm" && preview.callable > 0 && preview.heardFromUs === 0;
}

/**
 * The two angles a saved campaign's flow card follows. The warning that an
 * alert map has no report end is about what saving the draft needs, so it
 * follows the angle on screen. A rehearsal is run by the server on the stored
 * campaign, so the rehearse dialog follows the saved angle until the switch is
 * saved (an older server sends no angle: an offer).
 */
export function flowCardAngles(
  draft: Pick<CampaignDraft, "angle">,
  saved: Pick<AiCallCampaign, "angle">,
): { alert: boolean; rehearseAlert: boolean } {
  return { alert: draft.angle === "alert", rehearseAlert: saved.angle === "alert" };
}
